// reportPages/SectionChartsPages.js
import { captureChartCanvas } from './dataCalculations';
import { checkPageBreak } from './assetLoaders';
import moment from 'moment';
import { formatDuration } from '../../live/liveMetrics';
import { C, STATUS_RGB, hexToRgb, pageHeader, table, queueStatus, countStatus } from './pdfTheme';

const findSectionButtons = () => {
  // Method 1: Look for specific styled container
  const allDivs = document.querySelectorAll('div');
  for (const container of allDivs) {
    const computedStyle = window.getComputedStyle(container);
    const bgColor = computedStyle.backgroundColor;
    const borderRadius = computedStyle.borderRadius;
    const display = computedStyle.display;
    if (display === 'flex' && 
        bgColor === 'rgb(229, 231, 235)' && 
        borderRadius === '10px') {
      const buttons = container.querySelectorAll('button');
      if (buttons.length >= 1) {
        const firstButtonText = buttons[0].textContent.trim();
        if (firstButtonText.match(/Section\s+\d+/)) {
          return Array.from(buttons);
        }
      }
    }
  }

  // Method 2: Find all section buttons
  const allButtons = document.querySelectorAll('button');
  const sectionButtonsArray = Array.from(allButtons).filter(btn => {
    const text = btn.textContent.trim();
    return text.match(/^Section\s+\d+$/);
  });
  if (sectionButtonsArray.length >= 1) {
    return sectionButtonsArray;
  }

  // Method 3: Find flex containers with only section buttons
  const flexContainers = document.querySelectorAll('div');
  for (const container of flexContainers) {
    const computedStyle = window.getComputedStyle(container);
    if (computedStyle.display !== 'flex') continue;
    const buttons = container.querySelectorAll('button');
    if (buttons.length >= 1) {
      const sectionCount = Array.from(buttons).filter(btn => 
        btn.textContent.trim().match(/^Section\s+\d+$/)
      ).length;
      if (sectionCount === buttons.length && sectionCount >= 1) {
        return Array.from(buttons);
      }
    }
  }

  return null;
};

const findOriginalActiveButton = (sectionButtons) => {
  let originalActiveIndex = -1;
  sectionButtons.forEach((btn, idx) => {
    const btnStyle = window.getComputedStyle(btn);
    const bgColor = btnStyle.backgroundColor;
    const border = btnStyle.border;
    if (bgColor === 'rgb(255, 255, 255)' || border.includes('rgb(59, 130, 246)')) {
      originalActiveIndex = idx;
    }
  });
  return originalActiveIndex;
};

export const generateSectionChartsPages = async (pdf, options) => {
  const { setProgressMessage, setProgress, gameCards = [], isCountMode = false, formatTransactionTime = (v) => `${v}s` } = options;

  const pageWidth = pdf.internal.pageSize.getWidth();
  const margin = 15;

  await new Promise(resolve => setTimeout(resolve, 500));

  const sectionButtons = findSectionButtons();

  if (sectionButtons && sectionButtons.length > 0) {
    const originalActiveIndex = findOriginalActiveButton(sectionButtons);

    for (let tabIndex = 0; tabIndex < sectionButtons.length; tabIndex++) {
      const sectionButton = sectionButtons[tabIndex];
      const sectionName = sectionButton.textContent.trim();
      
      setProgressMessage(`Capturing ${sectionName} charts...`);
      sectionButton.click();
      await new Promise(resolve => setTimeout(resolve, 1000));

      pdf.addPage();
      const sectionId = sectionName.replace(/^Section\s+/, '');
      const games = gameCards.filter((g) => g.sections?.[sectionId]?.hasData);
      let yPos = pageHeader(
        pdf,
        sectionName,
        games.length ? `Compared across ${games.length} ${games.length === 1 ? 'game' : 'games'} over the time window.` : null
      );

      // Per-game stats for this section
      if (games.length) {
        const unit = isCountMode ? 'ppl' : 'min';
        const statusOf = (v, overflow) => (isCountMode ? countStatus(v, 15, 30) : queueStatus(v, overflow));
        const columns = [
          { header: 'Game', width: 50, render: (g) => ({ text: moment(g.date).format('ddd, MMM D'), swatch: hexToRgb(g.color), bold: true }) },
          { header: isCountMode ? 'Avg count' : 'Avg queue', align: 'center', render: (g) => { const m = g.sections[sectionId]; return { text: `${m.avg} ${unit}`, bold: true, color: STATUS_RGB[statusOf(m.avg)] }; } },
          { header: isCountMode ? 'Peak count' : 'Peak queue', align: 'center', render: (g) => { const m = g.sections[sectionId]; return { text: `${m.peak}${m.peakOverflow ? '+' : ''} ${unit}`, color: STATUS_RGB[statusOf(m.peak, m.peakOverflow)] }; } },
          { header: isCountMode ? 'Busy time' : 'Over target', align: 'center', render: (g) => { const m = g.sections[sectionId]; return { text: m.over > 0 ? formatDuration(m.over) : 'None', color: m.over > 0 ? C.bad : C.good }; } },
          ...(isCountMode ? [] : [
            { header: 'Overflow', align: 'center', render: (g) => { const m = g.sections[sectionId]; return m.overflow > 0 ? { text: formatDuration(m.overflow), color: C.bad, bold: true } : { text: 'None', color: C.subtle }; } },
            { header: 'Avg txn', align: 'center', render: (g) => formatTransactionTime(g.sections[sectionId].txnAvg) },
          ]),
        ];
        yPos = table(pdf, yPos, columns, games, { rowHeight: 8 });
        yPos += 8;
      }

      // Capture charts
      const sectionChartContainers = document.querySelectorAll('.graph.panel-2');
      
      if (sectionChartContainers.length === 0) {
        pdf.setFontSize(13); // Increased from 12
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(128, 128, 128);
        pdf.text(`No chart data available for ${sectionName}`, margin, yPos);
        yPos += 12;
      }

      for (let chartIdx = 0; chartIdx < sectionChartContainers.length; chartIdx++) {
        const chartContainer = sectionChartContainers[chartIdx];
        const titleElement = chartContainer.querySelector('p');
        const chartTitle = titleElement?.textContent || `Chart ${chartIdx + 1}`;

        const chartData = await captureChartCanvas(chartContainer);
        
        if (chartData) {
          const maxWidth = pageWidth - 2 * margin;
          const maxHeight = 95;
          let imgWidth = maxWidth;
          let imgHeight = imgWidth / chartData.aspectRatio;
          
          if (imgHeight > maxHeight) {
            imgHeight = maxHeight;
            imgWidth = imgHeight * chartData.aspectRatio;
          }

          yPos = checkPageBreak(pdf, yPos, margin + 8, imgHeight + 18);

          // Chart title
          pdf.setTextColor(C.text[0], C.text[1], C.text[2]);
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          pdf.text(chartTitle, margin, yPos);
          yPos += 5;

          // Add chart image
          const xOffset = margin + (maxWidth - imgWidth) / 2;
          pdf.addImage(chartData.dataUrl, 'PNG', xOffset, yPos, imgWidth, imgHeight, undefined, 'NONE');
          yPos += imgHeight + 10;
        }
      }

      setProgress(50 + (tabIndex + 1) / sectionButtons.length * 30);
    }

    // Restore original active button
    if (originalActiveIndex >= 0 && originalActiveIndex < sectionButtons.length) {
      sectionButtons[originalActiveIndex].click();
    }
  }
};